import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import TeacherSidebar from './TeacherSidebar';
import { useAuth } from '@/contexts/AuthContext';

const pageTitles: Record<string, string> = {
  '/teacher/dashboard': 'Öğretmen Dashboard',
  '/teacher/ogrencilerim': 'Bana Yönlendirilen Öğrenciler',
  '/teacher/deneme-derslerim': 'Deneme Derslerim',
  '/teacher/aktif-derslerim': 'Aktif Derslerim',
  '/teacher/ders-notlari': 'Ders Notları',
  '/teacher/dekont-girisleri': 'Dekont / Ödeme Girişleri',
  '/teacher/kazanclarim': 'Kazançlarım',
  '/teacher/profilim': 'Profilim',
};

export default function TeacherLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, loading, profile, logout } = useAuth();

  const title = pageTitles[location.pathname] ?? 'Öğretmen Paneli';
  const displayName = profile?.ad_soyad ?? 'Öğretmen';

  useEffect(() => {
    if (!loading && (!user || role !== 'ogretmen')) {
      navigate('/', { replace: true });
    }
  }, [user, role, loading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f5f3ff]">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-4xl text-[#7c6af5]/40 animate-spin" />
          <p className="text-slate-400 text-sm">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user || role !== 'ogretmen') return null;

  return (
    <div className="flex h-screen bg-[#f5f3ff] overflow-hidden">
      <TeacherSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Teacher Header */}
        <header className="h-16 bg-white border-b border-gray-100 flex items-center px-4 md:px-6 gap-4 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600 transition-colors cursor-pointer lg:hidden"
          >
            <i className="ri-menu-line text-xl" />
          </button>

          <div className="hidden md:block">
            <h2 className="text-base font-semibold text-gray-800">{title}</h2>
          </div>

          <div className="flex-1" />

          {/* Notifications */}
          <button className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600 transition-colors cursor-pointer">
            <i className="ri-notification-3-line text-xl" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
          </button>

          {/* Profile Dropdown */}
          <div className="relative group">
            <button className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-gray-100 transition-colors cursor-pointer">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#7c6af5] flex-shrink-0">
                <span className="text-white text-xs font-bold">{displayName.charAt(0)}</span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-semibold text-gray-800 leading-tight">{displayName}</p>
                <p className="text-xs text-gray-400 leading-tight">Öğretmen</p>
              </div>
              <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-arrow-down-s-line text-sm" />
              </div>
            </button>
            <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-2xl border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
                <p className="text-xs text-gray-400">Öğretmen</p>
              </div>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 w-full text-left cursor-pointer whitespace-nowrap">
                <i className="ri-logout-box-line" />
                Çıkış Yap
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}